import * as React from 'react';
import { useContext } from 'react';

import { type AuthActions, AuthActionsContext } from './AuthActionsContext';
import { initAuthContext, UNKNOWN_ANALYTICS_PREFIX } from './ctx/initAuthContext';
import { useOptionalContext, useProvidedContext } from './ctx/useProvidedContext';
import type {
  AuthResult,
  HumanStatusResponse,
  LogInCredentials,
  LogInResponse,
  PasswordlessTokenLoginParams,
  PasswordlessTokenLoginResponse,
  RegisterParams,
  RegisterResponse,
} from './sessionClient';
import type { User } from './User';

/** @public */
export type Authenticating = {
  state: 'authenticating';
  user: undefined;
};
/** @public */
export type NotAuthenticated = {
  state: 'not_authenticated';
  user: null;
};
type Authenticated = {
  state: 'authenticated';
  user: User;
};
export type AuthenticationError = {
  state: 'error';
  user: undefined;
  /** Message for developer. Do not show to the user. */
  message: string;
};

export type AuthenticationState =
  | Authenticating
  | NotAuthenticated
  | Authenticated
  | AuthenticationError;

/** @public */
export type EmailPasswordCredentials = {
  email: string;
  /** Password in plain text. Yes, in plain text :( */
  password: string;
};
/** @public */
export type GoogleCredentials = {
  googleIdToken: string;
};
/** @public */
export type FacebookCredentials = {
  facebookIdToken: string;
};

export type LoginCredentials = EmailPasswordCredentials | GoogleCredentials | FacebookCredentials;
/** @public */
export type LoginSuccess = { success: true };
/** @public */
export type LoginFailure = { success: false; displayErrorMessage: string };
export type LoginResult = LoginSuccess | LoginFailure;
/** @public */
export type ValidateResetPasswordTokenSuccess = { success: true };
/** @public */
export type ValidateResetPasswordTokenFailure = { success: false; displayErrorMessage?: string };
export type ValidateResetPasswordTokenResult =
  | ValidateResetPasswordTokenSuccess
  | ValidateResetPasswordTokenFailure;
/** @public */
export type ResetPasswordSuccess = { success: true };
/** @public */
export type ResetPasswordFailure = { success: false; displayErrorMessage?: string };
export type ResetPasswordResult = ResetPasswordSuccess | ResetPasswordFailure;

interface MarketingSmsConsent {
  /** The user's checkbox value: `true` opts in, `false` opts out. */
  optIn: boolean;
  /** Email expected on the authenticated account receiving the preference. */
  email: string;
  /** Stable version key naming the consent copy the user saw. */
  consentLanguage: string;
}

type RecordMarketingSmsConsentResult = { success: boolean };

export interface AuthContextState {
  authentication: AuthenticationState;
  /**
   * Logs the user out. The window will be refreshed.
   */
  logout: () => void;
  /**
   * Function to refresh the authentication state
   * The existing authentication state will remain the same until the refresh
   * finishes (i.e. the network calls complete again).
   * It's useful if a change in logged-in state is made that does not cause a
   * reload of this library (login/logout via. API, third party login, etc.)
   */
  refresh: () => void;
  /**
   * Attempts to verify login with the given credentials.
   * Note the user must be logged in to use this function.
   *
   * This can be called to refresh a session's last_login_at timestamp
   * to allow the user to perform more sensitive actions.
   *
   * See more documentation at:
   * https://knowledgebase.apps.opendoor.com/doc/product/customer-demand/product-eng-org/reference/last-login-authorization-feature.md
   */
  verifyLogin: (credentials: LoginCredentials) => Promise<LoginResult>;

  validateResetPasswordToken: (
    resetPasswordToken: string,
  ) => Promise<ValidateResetPasswordTokenResult>;

  resetPassword: (token: string, password: string) => Promise<ResetPasswordResult>;

  /**
   * The consuming app's analytics keyword — the APP segment of every
   * `APP-COMPONENT-ACTION` analytics name auth-fe emits (cosmos passes
   * `'cosmos'`, so its entry-screen email field is `cosmos-account-form-email`).
   * Supplied once where the app mounts its provider rather than threaded as a
   * prop: the reset-password modules and `ReauthForm` mount outside
   * `AccountFormProvider`, so the auth context is the only ancestor common to
   * every named control.
   *
   * Required, because a default here is what let five competing fallbacks
   * accumulate. A control needing a name off this scheme passes an explicit
   * `analyticsName`, which wins outright.
   */
  analyticsPrefix: string;

  /** Signs the user in against Rails `POST /login`; `data.redirectTo` carries the server's post-login destination. */
  logIn: (credentials: LogInCredentials) => Promise<AuthResult<LogInResponse>>;

  /**
   * Creates an account against Rails `POST /register`. Pass
   * `{ hasGeneratedPassword: true }` for the passwordless variant (Rails
   * generates the password and mails a setup link). `data.redirectTo` carries
   * the server's post-register destination.
   */
  register: (params: RegisterParams) => Promise<AuthResult<RegisterResponse>>;

  /**
   * Redeems a one-time passwordless token (login-lite / mobile-handoff link)
   * against Rails `POST /register`, logging the human in. `data.redirectTo`
   * carries the server's post-login destination.
   */
  loginWithPasswordlessToken: (
    params: PasswordlessTokenLoginParams,
  ) => Promise<AuthResult<PasswordlessTokenLoginResponse>>;

  /** Looks an email up against `GET /humans/status`; surfaces `isNewHuman`/`hasGeneratedPassword` for step routing. */
  requestHumanStatus: (email: string) => Promise<AuthResult<HumanStatusResponse>>;

  /**
   * Human-status-guarded reset: looks the email up, then only asks Rails to
   * send a reset/setup email for an existing account. A new email resolves
   * with `isNewHuman: true` and no email sent.
   */
  requestPasswordReset: (email: string) => Promise<AuthResult<HumanStatusResponse>>;

  /**
   * Records a marketing-SMS consent decision after registration. The consumer
   * owns the transport and must attribute the decision to the authenticated
   * account named by the registered email. Best-effort: resolves a typed result
   * and never throws, so a failed write can't turn a completed registration
   * into a failure. Only call this for a checkbox the user actually saw — a
   * hidden field has no decision to record, and synthesizing one is a compliance
   * bug.
   */
  recordMarketingSmsConsent: (
    consent: MarketingSmsConsent,
  ) => Promise<RecordMarketingSmsConsentResult>;
}

export const AuthContext = React.createContext<AuthContextState>(initAuthContext);

export interface UseAuth extends AuthContextState {
  /**
   * Convenience field for accessing `authentication.user`.
   * Returns User if authentication.state is `authenticated`.
   * Returns null if authentication.state is `not_authenticated`.
   * Returns undefined if authentication.state is `authenticating` or `error`.
   */
  user: User | null | undefined;
}

/**
 * The APP segment for composing an `APP-COMPONENT-ACTION` analytics name, with
 * the sentinel substituted for anything unusable.
 *
 * Every module reads the prefix through here rather than off `useAuth()`
 * directly, so the guard cannot be bypassed by a caller. It has to be a guard
 * and not a type: React context defaults are unreachable once any provider
 * mounts, so a provider supplied `''` (or, from a test file that TypeScript does
 * not check, nothing at all) would otherwise compose `-account-form-email` or
 * `undefined-account-form-email`.
 */
export function useAnalyticsPrefix(): string {
  return useAuthActions().analyticsPrefix || UNKNOWN_ANALYTICS_PREFIX;
}

/** Reads account actions; outside either provider, returns inert actions that report failure. */
export function useAuthActions(): AuthActions {
  const actions = useContext(AuthActionsContext);
  const auth = useContext(AuthContext);
  return actions ?? auth;
}

/** Gets AuthenticationState. Callable anywhere; falls back to the logged-out default outside a provider. */
export function useAuth(): UseAuth {
  const authContext = useContext(AuthContext);
  return {
    ...authContext,
    user: authContext.authentication.user,
  };
}

/**
 * Like {@link useAuth}, but throws outside an `AuthContext` provider instead of
 * inheriting the logged-out default. Use where a missing provider would be a
 * wiring bug — silently showing every user the signed-out state — rather than a
 * safe fallback.
 */
export function useSafeAuth(): UseAuth {
  const authContext = useProvidedContext(
    AuthContext,
    initAuthContext,
    'AuthSessionContextProvider',
  );
  return {
    ...authContext,
    user: authContext.authentication.user,
  };
}

/**
 * Like {@link useAuth}, but returns `undefined` outside an `AuthContext`
 * provider so a consumer can branch on provider presence rather than inheriting
 * the logged-out default.
 */
export function useOptionalAuth(): UseAuth | undefined {
  const authContext = useOptionalContext(AuthContext, initAuthContext);
  if (!authContext) {
    return undefined;
  }
  return {
    ...authContext,
    user: authContext.authentication.user,
  };
}

export interface UseAuthAuthenticated extends UseAuth {
  authentication: Authenticated;
  user: User;
}

/**
 * Gets AuthenticationState, but only if used in an authenticated context.
 *
 * This hook will guarantee that a user exists, but throws an error if not in an authenticated context.
 *
 * ```typescript
 * const Hello = () => {
 *   const { authentication } = useAuth();
 *   if (authentication.state === 'authenticated') {
 *     return <HelloAuthenticated />;
 *   } else {
 *     return <HelloMaybeAuthenticated />;
 *   }
 * };
 *
 * const HelloAuthenticated = () => {
 *   // GOOD. This will get the user and we don't have to check for null/undefined.
 *   const { user } = useAuthAuthenticated();
 *   return `Hello, ${user.name}`;
 * };
 *
 * const HelloMaybeAuthenticated = () => {
 *   // BAD. This will crash.
 *   const { user } = useAuthAuthenticated();
 *
 *   // GOOD. This will get the user but we must check for null/undefined before using it.
 *   const { user } = useAuth();
 *   if (user) {
 *     return `Hello, ${user.name}`;
 *   } else {
 *     return 'Hello, stranger';
 *   }
 * };
 * ```
 * @throws an error if not in an authenticated context
 */
export function useAuthAuthenticated(): UseAuthAuthenticated {
  const auth = useAuth();
  const { authentication } = auth;

  if (authentication.state === 'authenticated') {
    return { ...auth, authentication, user: authentication.user };
  } else {
    throw new Error(
      'DEVELOPER ERROR: useAuthAuthenticated must be used in an authenticated context',
    );
  }
}
